"use client"

import * as React from "react"
import { Drawer as DrawerPrimitive } from "vaul"

import { cn } from "@/lib/utils"

const Drawer = ({
    // Don't scale background - this sets body to pointer-events: none which blocks nav
    shouldScaleBackground = false,
    ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
    <DrawerPrimitive.Root
        shouldScaleBackground={shouldScaleBackground}
        // Don't use modal behavior - allows clicking outside without blocking
        modal={false}
        {...props}
    />
)
Drawer.displayName = "Drawer"

const DrawerTrigger = DrawerPrimitive.Trigger

const DrawerPortal = DrawerPrimitive.Portal

const DrawerClose = DrawerPrimitive.Close

const DrawerOverlay = React.forwardRef<
    React.ElementRef<typeof DrawerPrimitive.Overlay>,
    React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
    <DrawerPrimitive.Overlay
        ref={ref}
        className={cn(
            // Leave space at bottom for mobile nav (80px nav + 16px bottom offset + 16px margin)
            "fixed inset-0 z-50 bg-black/80 md:bottom-0",
            "bottom-[calc(80px+16px+16px)]", // Mobile: don't cover nav
            className
        )}
        {...props}
    />
))
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName

const DrawerContent = React.forwardRef<
    React.ElementRef<typeof DrawerPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content>
>(({ className, children, ...props }, ref) => (
    <DrawerPortal>
        {/* Overlay - non-interactive so nav bar beneath can be clicked */}
        <div
            className="fixed inset-0 z-40 bg-black/50 pointer-events-none"
            style={{ bottom: '80px' }} // Nav bar is 80px
        />
        <DrawerPrimitive.Content
            ref={ref}
            className={cn(
                // Position directly above nav bar - use max-height to prevent overflow
                "fixed inset-x-0 z-50 flex flex-col rounded-t-[10px] border bg-white dark:bg-gray-900 dark:border-gray-800",
                // Mobile: position from bottom of viewport minus nav height
                // max-height ensures content doesn't overflow
                className
            )}
            style={{
                bottom: '80px', // Position above nav bar
                maxHeight: 'calc(100vh - 80px - 20px)', // Leave space for nav + status bar
            }}
            {...props}
        >
            {/* Use proper Vaul Handle for drag-to-dismiss */}
            <DrawerPrimitive.Handle className="mx-auto mt-4 h-2 w-[100px] rounded-full bg-gray-300 dark:bg-gray-700" />
            {children}
        </DrawerPrimitive.Content>
    </DrawerPortal>
))
DrawerContent.displayName = "DrawerContent"

const DrawerHandle = DrawerPrimitive.Handle

const DrawerHeader = ({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className={cn("grid gap-1.5 p-4 text-center sm:text-left", className)}
        {...props}
    />
)
DrawerHeader.displayName = "DrawerHeader"

const DrawerFooter = ({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className={cn("mt-auto flex flex-col gap-2 p-4", className)}
        {...props}
    />
)
DrawerFooter.displayName = "DrawerFooter"

const DrawerTitle = React.forwardRef<
    React.ElementRef<typeof DrawerPrimitive.Title>,
    React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
    <DrawerPrimitive.Title
        ref={ref}
        className={cn(
            "text-lg font-semibold leading-none tracking-tight",
            className
        )}
        {...props}
    />
))
DrawerTitle.displayName = DrawerPrimitive.Title.displayName

const DrawerDescription = React.forwardRef<
    React.ElementRef<typeof DrawerPrimitive.Description>,
    React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
    <DrawerPrimitive.Description
        ref={ref}
        className={cn("text-sm text-gray-500 dark:text-gray-400", className)}
        {...props}
    />
))
DrawerDescription.displayName = DrawerPrimitive.Description.displayName

export {
    Drawer,
    DrawerPortal,
    DrawerOverlay,
    DrawerTrigger,
    DrawerClose,
    DrawerContent,
    DrawerHeader,
    DrawerFooter,
    DrawerTitle,
    DrawerDescription,
    DrawerHandle,
}
